import { useState } from "react";
import { toast } from "sonner";
import { Upload, Download } from "lucide-react";
import { descargarMapaCampos } from "@/components/copiloto/mapaCampos";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EJEMPLO_JSON, EJEMPLO_FAQ_JSON } from "@/config/copiloto";

const Resultado = ({ res }) => (
  <div className="mt-5" data-testid="import-result">
    {res.errores.length > 0 ? (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4">
        <div className="font-semibold text-red-800 text-sm">{res.errores.length} error(es). No se guardó nada.</div>
        <ul className="mt-2 text-xs text-red-700 space-y-1" data-testid="import-errores">
          {res.errores.map((e, i) => <li key={i}>Fila {e.fila}: {e.mensaje}</li>)}
        </ul>
      </div>
    ) : (
      <div className="rounded-xl border border-[#A7F3D0] bg-[#ECFDF5] p-3 text-sm text-[#065F46]">
        {res.importadas ? `${res.importadas} entradas importadas.` : `Todo válido: ${res.filas.length} entradas listas para importar.`}
      </div>
    )}
    {res.filas.length > 0 && (
      <ul className="mt-3 text-xs divide-y divide-[#E2DCD2] rounded-xl border border-[#E2DCD2] bg-white" data-testid="import-filas">
        {res.filas.map((f) => (
          <li key={f.fila} className="px-3 py-2 flex flex-wrap gap-2">
            <span className="text-slate-400">#{f.fila}</span>
            <span className={f.accion === "nuevo" ? "text-[#047857] font-semibold" : "text-[#92400E] font-semibold"}>{f.accion === "nuevo" ? "Nueva" : "Actualiza"}</span>
            <span className="text-slate-800">{f.titulo}</span>
            <span className="text-slate-500">{f.jurisdiccion} · {f.estado}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export const ImportPanel = () => {
  const [tipo, setTipo] = useState("why");
  const [formato, setFormato] = useState("json");
  const [contenido, setContenido] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (file) => {
    if (!file) return;
    setFormato(file.name.toLowerCase().endsWith(".csv") ? "csv" : "json");
    setContenido(await file.text());
    setRes(null);
  };
  const enviar = async (confirmar) => {
    setBusy(true);
    try {
      const { data } = await api.post("/copiloto/importar", { tipo, formato, contenido, confirmar });
      setRes(data);
      if (confirmar && data.ok) { toast.success(`${data.importadas} entradas importadas`); setContenido(""); }
    } catch (e) { toast.error(e.response?.data?.detail || "Error al importar"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl">
      <p className="text-sm text-slate-600">Sube un archivo JSON o CSV. Primero se revisa completo; si una sola fila tiene error, no se guarda nada.
        Lo importado entra como <b>Borrador</b> o <b>En revisión</b>; nunca se publica solo.</p>
      <div className="mt-4 flex flex-wrap gap-3 items-center">
        <Select value={tipo} onValueChange={(v) => { setTipo(v); setRes(null); }}>
          <SelectTrigger className="h-10 w-44" data-testid="import-tipo"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="why">Copiloto</SelectItem><SelectItem value="faq">FAQ</SelectItem></SelectContent>
        </Select>
        <Select value={formato} onValueChange={setFormato}>
          <SelectTrigger className="h-10 w-32" data-testid="import-formato"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="json">JSON</SelectItem><SelectItem value="csv">CSV</SelectItem></SelectContent>
        </Select>
        <label className="inline-flex items-center gap-2 h-10 px-3 rounded-lg border border-[#CBD5E1] bg-white text-sm cursor-pointer hover:bg-[#F3EFE6]">
          <Upload className="w-4 h-4" /> Subir archivo
          <input type="file" accept=".json,.csv" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="import-file" />
        </label>
        <Button variant="outline" className="h-10" onClick={() => { setFormato("json"); setContenido(tipo === "why" ? EJEMPLO_JSON : EJEMPLO_FAQ_JSON); setRes(null); }} data-testid="import-ejemplo">
          Ver ejemplo
        </Button>
        <Button variant="outline" className="h-10" onClick={() => descargarMapaCampos().catch(() => toast.error("No se pudo generar el mapa"))} data-testid="import-mapa-campos">
          <Download className="w-4 h-4 mr-1" /> Descargar mapa de campos
        </Button>
      </div>
      <textarea value={contenido} onChange={(e) => { setContenido(e.target.value); setRes(null); }} rows={12}
        placeholder="…o pega aquí el contenido" data-testid="import-contenido"
        className="mt-4 w-full rounded-xl border border-[#CBD5E1] p-3 font-mono text-xs bg-white" />
      <p className="mt-2 text-xs text-slate-500">
        Columnas Copiloto: document, fields, title, body (Cómo sí), dudas (Dudas reales), source (Fundamento legal), jurisdiccion, url_contenido, estado, id (opcional). FAQ: pregunta, respuesta, categoria, fuente, jurisdiccion, url_contenido, estado, id.
        En CSV separa varios campos con «|». Si el id ya existe, se actualiza y vuelve a revisión.
      </p>
      <div className="mt-4 flex gap-3">
        <Button variant="outline" disabled={busy || !contenido.trim()} onClick={() => enviar(false)} data-testid="import-revisar">Revisar</Button>
        <Button disabled={busy || !res?.ok || !!res?.importadas} onClick={() => enviar(true)} className="bg-[#1E3A5F] hover:bg-[#16304F]" data-testid="import-confirmar">
          Importar {res?.ok && !res?.importadas ? res.filas.length : ""}
        </Button>
      </div>
      {res && <Resultado res={res} />}
    </div>
  );
};
